import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { DoorOpen, Lock, ShieldAlert, ShieldCheck, History, ChevronDown, Wifi, WifiOff } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

function doorStatus(d) {
  if (d.online === false) return { label: "Offline", text: "text-brand-tertiary", bar: "#9CA3AF", badge: "bg-brand-ink/5 text-brand-tertiary" };
  if (d.locked_down) return { label: "Locked down", text: "text-rose-600", bar: "#DC2626", badge: "bg-rose-50 text-rose-600" };
  if (d.unlocked || d.on_scheduled_unlock || d.unlocked_until) return { label: "Unlocked", text: "text-emerald-600", bar: "#16A34A", badge: "bg-emerald-50 text-emerald-600" };
  return { label: "Locked", text: "text-slate-500", bar: "#64748B", badge: "bg-slate-100 text-slate-600" };
}

function DoorCard({ door, placeId, onUnlock }) {
  const [busy, setBusy] = useState(false);
  const offline = door.online === false;
  const st = doorStatus(door);
  const isUnlocked = Boolean(door.unlocked || door.on_scheduled_unlock || door.unlocked_until);
  const canUnlock = !offline && !door.locked_down && !isUnlocked;
  const unlock = async () => {
    if (!window.confirm(`Unlock “${door.name}”? This physically opens the door.`)) return;
    setBusy(true);
    try { await onUnlock(door.id, placeId); toast.success(`Unlock sent to ${door.name}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Unlock failed"); }
    finally { setBusy(false); }
  };
  const scheduled = door.on_scheduled_unlock && !door.unlocked_until;
  return (
    <div className="flex items-center justify-between gap-3 border border-brand-border rounded-lg pl-0 pr-3 py-2.5 bg-brand-surface overflow-hidden"
      data-testid={`door-${door.id}`} style={{ borderLeft: `3px solid ${st.bar}` }}>
      <div className="min-w-0 flex items-center gap-2 pl-3">
        {offline ? <WifiOff className="h-3.5 w-3.5 text-brand-tertiary shrink-0" />
          : <Wifi className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
        <div className="min-w-0">
          <span className="text-sm text-brand-ink truncate block leading-tight">{door.name}</span>
          <span className={`text-[10px] font-medium ${st.text}`} data-testid={`door-status-${door.id}`}>
            {st.label}
            {isUnlocked && scheduled ? " · on schedule" : ""}
            {door.open && !door.locked_down ? " · ajar" : ""}
          </span>
        </div>
      </div>
      {canUnlock ? (
        <button onClick={unlock} disabled={busy} data-testid={`door-unlock-${door.id}`}
          className="shrink-0 inline-flex items-center gap-1.5 text-xs rounded-md px-2.5 py-1.5 bg-brand-ink text-brand-page disabled:opacity-40 hover:opacity-90 transition-opacity">
          <DoorOpen className="h-3.5 w-3.5" /> {busy ? "Sending…" : "Unlock"}
        </button>
      ) : (
        <span data-testid={`door-state-${door.id}`}
          className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-medium rounded-md px-2.5 py-1.5 ${st.badge}`}>
          {door.locked_down ? <ShieldAlert className="h-3.5 w-3.5" />
            : isUnlocked ? <DoorOpen className="h-3.5 w-3.5" />
              : offline ? <WifiOff className="h-3.5 w-3.5" />
                : <Lock className="h-3.5 w-3.5" />}
          {st.label}
        </span>
      )}
    </div>
  );
}

function AccessLog({ placeId }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const toggle = async () => {
    const nv = !open; setOpen(nv);
    if (nv && events === null) {
      setLoading(true);
      try { const { data } = await api.get("/kisi/events", { params: { place_id: placeId, days: 7 } }); setEvents(data.events || []); }
      catch (e) { setEvents([]); toast.error("Door activity couldn't load", { description: e.userMessage }); }
      finally { setLoading(false); }
    }
  };
  return (
    <div className="mt-3">
      <button onClick={toggle} data-testid={`door-log-toggle-${placeId}`}
        className="inline-flex items-center gap-1.5 text-xs text-brand-muted hover:text-brand-ink">
        <History className="h-3.5 w-3.5" /> Access log (7 days)
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 border border-brand-border rounded-lg divide-y divide-brand-border max-h-56 overflow-y-auto" data-testid={`door-log-${placeId}`}>
          {loading ? <div className="p-3 text-xs text-brand-tertiary">Loading…</div>
            : !events?.length ? <div className="p-3 text-xs text-brand-tertiary">No recent access events.</div>
              : events.slice(0, 100).map((e, i) => (
                <div key={e.id || i} className="px-3 py-2 flex items-center justify-between gap-3 text-xs">
                  <span className="text-brand-ink truncate">{e.actor}</span>
                  <span className="text-brand-muted truncate">{e.object}</span>
                  <span className="text-brand-tertiary shrink-0">{e.at ? new Date(e.at).toLocaleString() : ""}</span>
                </div>
              ))}
        </div>
      )}
    </div>
  );
}

function PlaceBlock({ group, onUnlock, showHeader, onChanged }) {
  const [locking, setLocking] = useState(false);
  const inLockdown = group.locks.some((l) => l.locked_down);
  const act = async (kind) => {
    const label = kind === "lockdown" ? "LOCK DOWN all doors at" : "Cancel lockdown at";
    if (!window.confirm(`${label} ${group.location}?`)) return;
    setLocking(true);
    try {
      await api.post(`/kisi/places/${group.place_id}/${kind}`);
      toast.success(kind === "lockdown" ? "Lockdown triggered" : "Lockdown cancelled");
      setTimeout(onChanged, 1200);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); }
    finally { setLocking(false); }
  };
  return (
    <section data-testid={`door-place-${group.place_id}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        {showHeader ? <h3 className="font-serif text-xl text-brand-ink">{group.location}</h3> : <span />}
        <div className="flex items-center gap-2">
          {inLockdown ? (
            <button onClick={() => act("cancel_lockdown")} disabled={locking} data-testid={`door-cancel-${group.place_id}`}
              className="inline-flex items-center gap-1.5 text-xs rounded-md px-3 py-1.5 bg-rose-600 text-white hover:bg-rose-700 transition-colors">
              <ShieldCheck className="h-3.5 w-3.5" /> Cancel lockdown
            </button>
          ) : (
            <button onClick={() => act("lockdown")} disabled={locking} data-testid={`door-lockdown-${group.place_id}`}
              className="inline-flex items-center gap-1.5 text-xs rounded-md px-3 py-1.5 border border-brand-border text-brand-muted hover:border-rose-300 hover:text-rose-600 transition-colors">
              <ShieldAlert className="h-3.5 w-3.5" /> Lock down
            </button>
          )}
        </div>
      </div>
      {group.locks.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
          {group.locks.map((d) => <DoorCard key={d.id} door={d} placeId={group.place_id} onUnlock={onUnlock} />)}
        </div>
      ) : <p className="text-sm text-brand-tertiary"><Lock className="h-3.5 w-3.5 inline mr-1" />No doors found.</p>}
      <AccessLog placeId={group.place_id} />
    </section>
  );
}

export default function DoorsPanel({ location = null, showHeaders = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kisi/doors", { params: location ? { location } : {} });
      setConfigured(data.configured);
      setData(data.locations || []); setLoadError(null);
    } catch (e) { setLoadError(e.userMessage); }
    finally { setLoading(false); }
  }, [location]);

  useEffect(() => { load(); }, [load]);

  // Keep door status live.
  useEffect(() => {
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const onUnlock = async (lockId, placeId) => {
    const r = await api.post(`/kisi/locks/${lockId}/unlock`, null, { params: { place_id: placeId } });
    setTimeout(load, 1200);
    return r;
  };

  if (loading) return <div className="grid sm:grid-cols-2 gap-2" data-testid="doors-loading">{[...Array(4)].map((_, i) => <div key={i} className="h-12 rounded-lg bg-brand-surface border border-brand-border animate-pulse" />)}</div>;
  if (loadError && !data?.length) return <div className="text-sm text-brand-neg" data-testid="doors-error">Doors couldn't load: {loadError} Retrying every 15 seconds.</div>;
  if (!configured) return <div className="text-sm text-brand-muted" data-testid="doors-not-configured">No Kisi API key saved yet — door controls are off.</div>;
  if (!data?.length) return <div className="text-sm text-brand-muted py-6 text-center" data-testid="doors-empty">No doors available{location ? " for this location" : ""}.</div>;

  return (
    <div className="space-y-8" data-testid="doors-panel">
      <div className="flex items-center gap-4 text-[11px] text-brand-muted">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: "#64748B" }} /> Locked</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: "#16A34A" }} /> Unlocked</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: "#DC2626" }} /> Locked down</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: "#9CA3AF" }} /> Offline</span>
        <span className="text-brand-tertiary ml-auto">Auto-refreshes every 15s</span>
      </div>
      {data.map((g) => <PlaceBlock key={g.place_id} group={g} onUnlock={onUnlock} showHeader={showHeaders} onChanged={load} />)}
    </div>
  );
}
